import {
  processSocket,
  useAutoTranslation,
  useProcessSocketStore,
} from "@/entities"
import { useProcessCreationLoadingStore } from "@/entities/process-creation-loading"
import {
  ALLOWED_EXTENSIONS,
  ALLOWED_IMAGE_EXTENSIONS,
  ALLOWED_IMAGE_MIME_TYPES,
  DISCOVERED_TRANSLATION_KEYS,
} from "@/shared/constants"
import { uploadBrowserContent } from "@/shared/api/content-upload"
import { getFileExtension, isSupportedImageFile } from "@/shared/utils"
import { toast } from "sonner"
import { useCreateNoteImages } from "../use-create-note-images"

const allowed_type = [
  "application/pdf", // PDF files
  "text/plain", // TXT files
  "application/msword", // DOC files
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // DOCX files
  "application/rtf", // RTF files
  "application/epub+zip", // EPUB files
  "text/html", // HTML files
  "text/x-web-markdown", // Markdown files
  "text/rtf",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.oasis.opendocument.text",
  "text/csv",
  "application/csv",
]

const createTriggerInput = () => {
  const input = document.createElement("input")
  input.type = "file"
  input.accept = [
    ...allowed_type,
    ...ALLOWED_EXTENSIONS.map(ext => `.${ext}`),
    ...ALLOWED_IMAGE_MIME_TYPES,
    ...ALLOWED_IMAGE_EXTENSIONS.map(ext => `.${ext}`),
  ].join(",")
  input.click()
  return input
}

export function useCreateNoteUploadFile() {
  const { handleProcessImages } = useCreateNoteImages()
  const { t } = useAutoTranslation()
  const handleFileUpload = async () => {
    const input = createTriggerInput()

    input.onchange = async e => {
      input.remove()
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return
      handleProcessFile(file)
    }
  }

  const handleProcessFile = async (file: File) => {
    if (isSupportedImageFile(file)) {
      await handleProcessImages([file])
      return
    }

    const fileExtension = getFileExtension(file)
    if (!fileExtension) {
      toast.error(t(DISCOVERED_TRANSLATION_KEYS.INVALID_FILE_TYPE))
      return
    }

    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      toast.error(t(DISCOVERED_TRANSLATION_KEYS.FILE_TYPE_NOT_SUPPORTED))
      return
    }

    // trigger create note loader
    useProcessCreationLoadingStore.getState().startAppLoading()

    try {
      await useProcessSocketStore.getState().connectToProcessSocket()
      await uploadBrowserContent({
        kind: "FILE",
        files: [file],
        socketClientId: processSocket.id,
      })
    } catch (error) {
      useProcessCreationLoadingStore.getState().closeAppLoading()
      toast.error(error instanceof Error ? error.message : "Upload failed")
    }
  }

  return { handleFileUpload, handleProcessFile }
}
